import { useCallback, useEffect, useRef, useState } from 'react'

import type { CheckMaskImagesReadyResponse } from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'

/**
 * マスクポーリングのフェーズ
 * - initial: 初期待機中（20秒）
 * - polling: ポーリング中
 * - ready: マスク準備完了
 * - timeout: タイムアウト（90秒経過、ボタン有効化）
 */
export type MaskPollingPhase = 'initial' | 'polling' | 'ready' | 'timeout'

// ポーリング設定
const INITIAL_WAIT_MS = 20000 // 初期待機時間（20秒）
const TIMEOUT_MS = 90000 // 最大待機時間（90秒）
const POLLING_INTERVAL_MS = 2000 // ポーリング間隔（2秒）

interface UseMaskPollingProps {
  /** ポーリングを開始するかどうか（マスク画像がない場合にtrue） */
  shouldStartPolling: boolean
  /** マスク準備状態をチェックする関数 */
  checkMaskReady?: () => Promise<CheckMaskImagesReadyResponse>
  /** 外部からマスク準備完了を検知した場合（既存のimages変更検知との併用） */
  externalMaskReady?: boolean
  /** マスク準備完了時に呼ばれるコールバック（マスクURLのみを取得するために使用） */
  onMaskReady?: () => void
}

interface UseMaskPollingReturn {
  /** 現在のフェーズ */
  phase: MaskPollingPhase
  /** ポーリングを停止する */
  stopPolling: () => void
  /** ポーリングをリセットする（新しい画像アップロード時など） */
  resetPolling: () => void
}

/**
 * マスク画像生成完了をポーリングで確認するフック
 *
 * 動作フロー:
 * 1. shouldStartPolling=true になったら、20秒の初期待機開始
 * 2. 初期待機後、2秒間隔でポーリング開始
 * 3. マスク完了 or 90秒タイムアウトで終了
 */
export const useMaskPolling = ({
  shouldStartPolling,
  checkMaskReady,
  externalMaskReady,
  onMaskReady,
}: UseMaskPollingProps): UseMaskPollingReturn => {
  const [phase, setPhase] = useState<MaskPollingPhase>('ready')

  // refs for timer management
  const initialTimerRef = useRef<NodeJS.Timeout | null>(null)
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const isPollingActiveRef = useRef(false)

  // checkMaskReadyをRefに保存して、依存配列から削除することで無限ループを防ぐ
  // 関数が変わっても、Refを通じて常に最新の関数を参照する
  const checkMaskReadyRef = useRef(checkMaskReady)
  checkMaskReadyRef.current = checkMaskReady

  // タイマーをクリアするヘルパー
  const clearAllTimers = useCallback(() => {
    if (initialTimerRef.current) {
      clearTimeout(initialTimerRef.current)
      initialTimerRef.current = null
    }
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  // ポーリングを停止
  const stopPolling = useCallback(() => {
    clearAllTimers()
    isPollingActiveRef.current = false
  }, [clearAllTimers])

  // ポーリングをリセット
  const resetPolling = useCallback(() => {
    stopPolling()
    setPhase('ready')
    startTimeRef.current = null
  }, [stopPolling])

  // 外部からマスク準備完了を検知した場合
  useEffect(() => {
    if (externalMaskReady && phase !== 'ready') {
      stopPolling()
      setPhase('ready')
    }
  }, [externalMaskReady, phase, stopPolling])

  // ポーリングが完了してphaseが'ready'になったときにonMaskReadyを呼び出す
  const prevPhaseRef = useRef<MaskPollingPhase>(phase)
  useEffect(() => {
    const prevPhase = prevPhaseRef.current
    prevPhaseRef.current = phase

    // ポーリング中('initial'または'polling')から'ready'に変わった場合のみ呼び出す
    if (phase === 'ready' && (prevPhase === 'initial' || prevPhase === 'polling')) {
      onMaskReady?.()
    }
  }, [phase, onMaskReady])

  // ポーリングを実行する関数
  // checkMaskReadyRefを使用することで依存配列から削除し、無限ループを防ぐ
  const executePolling = useCallback(async () => {
    const checkFn = checkMaskReadyRef.current
    if (!isPollingActiveRef.current || !checkFn) {
      return
    }

    // タイムアウトチェック
    if (startTimeRef.current && Date.now() - startTimeRef.current > TIMEOUT_MS) {
      setPhase('timeout')
      isPollingActiveRef.current = false
      return
    }

    try {
      const response = await checkFn()
      if (response.isReady) {
        setPhase('ready')
        isPollingActiveRef.current = false
        return
      }
    } catch {
      // エラー時は次回ポーリングで再試行
    }

    // 次回ポーリングをスケジュール（2秒間隔）
    if (isPollingActiveRef.current) {
      pollingTimerRef.current = setTimeout(() => {
        executePolling()
      }, POLLING_INTERVAL_MS)
    }
  }, [])

  // shouldStartPolling が true になったらポーリング開始
  useEffect(() => {
    // ポーリング不要（既にマスクがある）の場合
    if (!shouldStartPolling) {
      // ポーリング中だったらリセット
      if (isPollingActiveRef.current || phase === 'initial' || phase === 'polling') {
        resetPolling()
      }
      return
    }

    // 既にポーリング中の場合はスキップ
    if (isPollingActiveRef.current) {
      return
    }

    // タイムアウト済みの場合はスキップ（ユーザーが手動実行できる状態）
    if (phase === 'timeout') {
      return
    }
    // ここに到達するのは shouldStartPolling=true かつ phase='ready' の場合のみ
    // → 新規ポーリングを開始する

    // checkMaskReady が提供されていない場合は、初期待機のみ行う
    // （既存の画像変更検知で完了を判定するケース）
    const checkFn = checkMaskReadyRef.current
    if (!checkFn) {
      setPhase('initial')
      startTimeRef.current = Date.now()
      isPollingActiveRef.current = true

      initialTimerRef.current = setTimeout(() => {
        if (!isPollingActiveRef.current) return
        // 初期待機後はタイムアウト状態へ（APIがない場合）
        setPhase('timeout')
        isPollingActiveRef.current = false
      }, TIMEOUT_MS)

      return () => {
        clearAllTimers()
      }
    }

    // ポーリング開始
    setPhase('initial')
    startTimeRef.current = Date.now()
    isPollingActiveRef.current = true

    // 初期待機の前にまず1回チェック（既に完了している場合に即座に対応）
    // リロードなどで既にマスク生成が終わっているケースに対応
    ;(async () => {
      try {
        const currentCheckFn = checkMaskReadyRef.current
        if (!currentCheckFn) {
          return
        }

        const response = await currentCheckFn()
        if (response.isReady) {
          setPhase('ready')
          isPollingActiveRef.current = false
          return
        }
      } catch {
        // エラー時は無視して初期待機へ進む
      }

      // まだ未完了の場合、初期待機後にポーリング開始
      if (!isPollingActiveRef.current) {
        return
      }
      initialTimerRef.current = setTimeout(() => {
        if (!isPollingActiveRef.current) {
          return
        }
        setPhase('polling')
        executePolling()
      }, INITIAL_WAIT_MS)
    })()

    return () => {
      clearAllTimers()
    }
    // NOTE: checkMaskReady, executePolling, phaseを依存配列から削除
    // checkMaskReadyはRefを通じて参照するため、依存配列に含める必要がない
    // これにより関数の再作成による無限ループを防ぐ
  }, [shouldStartPolling, resetPolling, clearAllTimers, executePolling])

  return {
    phase,
    stopPolling,
    resetPolling,
  }
}
